// „По станции" — стакната лента с дела на всяка станция + списък с проценти
// и суми. Сегментите са в амбър палитрата на приложението (споделената
// `methodColor`), за да са различими съседните, без да излизат от системата.

import { useTranslation } from "react-i18next";

import { useReportLink } from "../useReportLink";
import { RightOutlined } from "@ant-design/icons";

import { fmtEuro } from "../../../../utils/currency";
import { parseDecimal } from "../../../../components/dataTable";
import { methodColor } from "../../../../utils/paymentMethodsDisplay";
import type { DashboardStationAmount } from "../../../../api/reportsDashboard";

type Props = { stations: DashboardStationAmount[] };

export const StationsShareCard = ({ stations }: Props) => {
  const { t } = useTranslation();
  const openReport = useReportLink("stations");

  const rows = stations.map((station, index) => ({
    ...station,
    value: parseDecimal(station.amount),
    color: methodColor(index),
  }));
  const total = rows.reduce((sum, station) => sum + station.value, 0);

  return (
    <section className="flex min-w-0 flex-col rounded-xl border border-shade-2 bg-surface px-4 py-3.5">
      <header className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
          {t("reportsPage.dashStationsTitle")}
        </h2>
        {openReport ? (
          <button
            type="button"
            onClick={openReport}
            className="flex items-center gap-1 text-[12px] font-bold text-brand-deep-ink hover:underline"
          >
            {t("reportsPage.dashFullReport")}
            <RightOutlined className="text-[9px]" />
          </button>
        ) : null}
      </header>

      {rows.length && total > 0 ? (
        <>
          <div className="mb-3 flex h-2 overflow-hidden rounded-full bg-fill-2">
            {rows.map((station) => (
              // Ширината и цветът идват от данните — единственият позволен
              // inline style тук.
              <span
                key={station.station_id}
                title={`${station.name} · ${fmtEuro(station.value)}`}
                style={{
                  width: `${(station.value / total) * 100}%`,
                  background: station.color,
                }}
              />
            ))}
          </div>
          <ul className="flex flex-col gap-2">
            {rows.map((station) => (
              <li
                key={station.station_id}
                className="flex items-center gap-2.5"
              >
                <span
                  aria-hidden
                  className="size-[9px] shrink-0 rounded-full"
                  style={{ background: station.color }}
                />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink">
                  {station.name}
                </span>
                <span className="font-pos-mono text-[12px] font-semibold tabular-nums text-muted">
                  {`${Math.round((station.value / total) * 100)} %`}
                </span>
                <span className="w-[86px] shrink-0 text-right font-pos-mono text-[12.5px] font-bold tabular-nums text-ink">
                  {fmtEuro(station.value)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="py-6 text-center text-[12.5px] font-medium text-muted">
          {t("reportsPage.dashNoData")}
        </p>
      )}
    </section>
  );
};
